import { datadogRum } from '@datadog/browser-rum';

/**
 * Datadog RUM utility functions for tracking user actions, errors, and context
 */

// Enable debug logging in development
const isDevelopment = process.env.NODE_ENV === 'development';

const logDatadogEvent = (eventType: string, eventName: string, data?: any) => {
  if (isDevelopment) {
    console.log(`[Datadog ${eventType}]`, eventName, data || '');
  }
};

/**
 * Track a custom user action
 */
export const trackAction = (name: string, context?: Record<string, any>): void => {
  logDatadogEvent('Action', name, context);
  if (!datadogRum) return;
  datadogRum.addAction(name, context);
};

/**
 * Track an error with additional context
 */
export const trackError = (error: Error, context?: Record<string, any>): void => {
  logDatadogEvent('Error', error.message, { ...context, stack: error.stack });
  if (!datadogRum) return;
  datadogRum.addError(error, context);
};

/**
 * Add custom timing for performance tracking
 */
export const addTiming = (name: string): void => {
  logDatadogEvent('Timing', name);
  if (!datadogRum) return;
  datadogRum.addTiming(name);
};

/**
 * Track API errors with request details
 */
export const trackApiError = (
  error: Error,
  url: string,
  method: string,
  statusCode?: number,
  responseBody?: any
): void => {
  logDatadogEvent('API Error', error.message, {
    url,
    method,
    statusCode,
    responseBody: responseBody ? JSON.stringify(responseBody).substring(0, 200) : undefined,
  });

  if (!datadogRum) return;

  datadogRum.addError(error, {
    type: 'api_error',
    url,
    method,
    statusCode,
    responseBody: responseBody ? JSON.stringify(responseBody).substring(0, 200) : undefined,
  });
};

/**
 * Track authentication events
 */
export const trackAuthEvent = (
  event: 'login_attempt' | 'login_success' | 'login_failure' | 'forgot_password_click',
  context?: Record<string, any>
): void => {
  logDatadogEvent('Auth Event', `auth.${event}`, context);

  if (!datadogRum) return;

  datadogRum.addAction(`auth.${event}`, {
    ...context,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track form validation errors
 */
export const trackValidationError = (field: string, error: string): void => {
  logDatadogEvent('Validation Error', 'form.validation_error', { field, error });

  if (!datadogRum) return;

  datadogRum.addAction('form.validation_error', {
    field,
    error,
  });
};

/**
 * Check if Datadog RUM is initialized and ready
 */
export const isDatadogReady = (): boolean => {
  return !!datadogRum;
};

/**
 * Add global context that persists across all events
 */
export const addGlobalContext = (key: string, value: any): void => {
  logDatadogEvent('Global Context', `Set ${key}`, value);

  if (!datadogRum) return;
  datadogRum.setGlobalContextProperty(key, value);
};

/**
 * Remove global context
 */
export const removeGlobalContext = (key: string): void => {
  logDatadogEvent('Global Context', `Remove ${key}`);

  if (!datadogRum) return;
  datadogRum.removeGlobalContextProperty(key);
};

